import { Fragment } from 'react';

import Banner from './components/Layout/Banner/Banner';
import Header from './components/Layout/Header/Header';
import HeaderLogo from './components/Layout/Header/HeaderLogo';
import Footer from './components/Layout/Footer/Footer';
import HomePage from './pages/HomePage';
import PodsPage from './pages/PodsPage';
import DirectoryPage from './pages/DirectoryPage';
import MentorDirectoryPage from './pages/MentorDirectoryPage';
import SwagStorePage from './pages/SwagStorePage';
import Pre100Page from './pages/Pre100Page';
import SwagPage from './components/UI/SwagPage/SwagPage';
import JoinTeamPage from './pages/JoinTeamPage';
import JoinedEmailPage from './pages/JoinedEmailPage';
import DealsPage from './pages/DealsPage';
import DealsTnCPage from './pages/DealsTnCPage';
import OnboardingPage from './pages/OnboardingPage';
import MentorRegistrationPage from './pages/MentorRegistrationPage';
import StoriesPage from './pages/StoriesPage';
import RelaunchPage from './pages/RelaunchPage';
import EventsPage from './pages/EventsPage';
import SaaSLandingPage from './pages/SaaSLandingPage';
import FreeTrialLandingPage from './pages/FreeTrialLandingPage';
import PaymentTrialLandingPage from './pages/PaymentTrialLandingPage';
import ArticleLinks from './components/UI/ArticleLinks/ArticleLinks';
import ProfilePage from './components/UI/ProfilePage/ProfilePage';
import MentorPage from './components/UI/MentorPage/MentorPage';
import ProfileLinks from './components/UI/ProfileLinks/ProfileLinks';
import UserDashboard from './components/UI/UserDashboard/UserDashboard';
import CartPage from './pages/Cart';
import ResourcesPage from './pages/ResourcesPage';
import PaymentsPage from './pages/PaymentsPage';
import VCFFormPage from './pages/VCFFormPage';
import Mastermind from './pages/Mastermind';
import Location from './pages/Location';
import SignoutRedirectPage from './pages/SignoutRedirectPage';
import PrivacyPage from './pages/PrivacyPage';
import AboutPage from './pages/AboutPage';
import NewsletterPage from './pages/NewsletterPage';
import TermsAndConditions from './pages/TermsAndConditions';

import FormDialog from './components/UI/FormDialog/FormDialog';
import ScrollToTop from './misc/scrollToTop';

import { PROFILE_TABLE } from './constants/supabase_constants';

import { Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from './api';
import { useEffect, useState } from 'react';
import SignIn from './pages/SupabaseLogin';
import SignUp from './pages/SupabaseSignUp';
import SetPassword from './pages/SupabaseSetPassword';
import ResetPassword from './pages/SupabaseResetPassword';

import { createTheme, ThemeProvider, responsiveFontSizes, Box } from '@mui/material';

let theme = createTheme({
  palette: {
    type: 'light',
    primary: {
      main: '#00203F',
    },
    secondary: {
      main: '#47D68F',
    },
    text: {
      primary: '#00203F',
    },
    info: {
      main: '#2E5994',
    },
  },
  typography: {
    fontFamily: ['Montserrat'].join('', ''),
    body1: {
      fontFamily: 'Inter',
      fontSize: 18,
      fontWeight: 400,
      letterSpacing: -0.3,
    },
    body2: {
      fontFamily: 'Inter',
    },
    h6: {
      letterSpacing: -0.3,
      lineHeight: 1.4,
    },
    caption: {
      fontFamily: 'Inter',
    },
  },
});

const styles = {
  container: {
    width: { xs: '90%', sm: '80%' },
    margin: '2.5rem auto',
  },
};

theme = responsiveFontSizes(theme);

function App() {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState({});
  let [timedPopup, setTimedPopup] = useState(false);
  const location = useLocation();
  let navigate = useNavigate();

  useEffect(
    () => {
      setSession(supabase.auth.session());

      supabase.auth.onAuthStateChange((event, session) => {
        setSession(session);
        if (event === 'PASSWORD_RECOVERY') {
          navigate('setPassword', { replace: true });
        }
      });

      const user = supabase.auth.user();
      if (user) {
        getProfile(user);
      }
    },
    // eslint-disable-next-line
    [session],
  );

  useEffect(() => {
    const modalKey = 'modalSession';
    const modalSession = localStorage.getItem(modalKey);
    if (!modalSession) {
      setTimeout(() => {
        if (location.pathname.startsWith('/profileLinks')) return;
        setTimedPopup(true);
      }, 10000);
    }
  }, [session, location.pathname]);

  const hideModal = () => {
    const modalKey = 'modalSession';
    localStorage.setItem(modalKey, 'true');
    setTimedPopup(false);
  };

  const getProfile = async (user) => {
    try {
      let { data, error, status } = await supabase.from(PROFILE_TABLE).select(`*`).eq('id', user.id).single();

      if (error && status !== 406) {
        throw error;
      }

      if (data) {
        setUser(data);
      }
    } catch (error) {
      alert(error.message);
    } finally {
      console.log(user);
    }
  };

  return (
    <main>
      {location.pathname.startsWith('/freeTrial') ? (
        <Routes>
          <Route path='/freeTrial' element={<FreeTrialLandingPage />} />
          <Route path='/freeTrialInvited' element={<PaymentTrialLandingPage />} />
        </Routes>
      ) : (
        <ThemeProvider theme={theme}>
          <ScrollToTop />
          <Fragment>
            <Box>
              {location.pathname.startsWith('/onboarding') ||
              location.pathname.startsWith('/mentorRegistration') ||
              location.pathname.startsWith('/profileLinks') ||
              location.pathname.startsWith('/newsletter') ? (
                <HeaderLogo />
              ) : (
                <Fragment>
                  <Banner termsAndConditions={Object.keys(user).length === 0} />
                  <Box sx={styles.container}>
                    <Header session={session} />
                  </Box>
                </Fragment>
              )}
              <main>
                {user && <FormDialog trigger={timedPopup} setTrigger={hideModal} />}
                <Box>
                  <Routes>
                    <Route path='/' element={<HomePage />} />
                  </Routes>
                </Box>
                <Box sx={styles.container}>
                  <Routes>
                    <Route path='/store' element={<SwagStorePage session={session} user={user} />} />
                    <Route path='/store/:id' element={<SwagPage />} />
                    <Route path='/directory' element={<DirectoryPage session={session} user={user} />} />
                    <Route path='/mentors' element={<MentorDirectoryPage session={session} user={user} />} />
                    <Route path='/mentors/:id' element={<MentorPage />} />
                    <Route path='/directory/:id' element={<ProfilePage />} />
                    <Route path='/100saas' element={<SaaSLandingPage />} />
                    <Route path='/pods' element={<PodsPage />} />
                    <Route path='/about' element={<AboutPage />} />
                    <Route path='/profileLinks/:id' element={<ProfileLinks />} />
                    <Route path='/pre100' element={<Pre100Page />} />
                    <Route path='/joinTheTeam' element={<JoinTeamPage />} />
                    <Route path='/joinEmail' element={<JoinedEmailPage />} />
                    <Route path='/resources' element={<ResourcesPage />} />
                    <Route path='/payments' element={<PaymentsPage />} />
                    <Route path='/deals' element={<DealsPage user={user} />} />
                    <Route path='/deals/tnc' element={<DealsTnCPage />} />
                    <Route path='/stories' element={<StoriesPage user={user} />} />
                    <Route path='/events' element={<EventsPage />} />
                    <Route path='/relaunch' element={<RelaunchPage />} />
                    <Route path='/onboarding' element={<OnboardingPage />} />
                    <Route path='/mentorRegistration' element={<MentorRegistrationPage />} />
                    <Route path='/mentorRegistration/:id' element={<MentorRegistrationPage />} />
                    <Route path='/onboarding/:id' element={<OnboardingPage />} />
                    <Route path='/stories/:id' element={<ArticleLinks />} />
                    <Route path='/stories/:id/:slug' element={<ArticleLinks />} />
                    <Route path='/vcf_form' element={!session ? <SignIn /> : <VCFFormPage />} />
                    <Route path='/join' element={!session ? <SignUp /> : <Navigate to='/dashboard' />} />
                    <Route path='/login' element={!session ? <SignIn /> : <Navigate to='/dashboard' />} />
                    <Route path='/setPassword' element={<SetPassword />} />
                    <Route path='/resetPassword' element={<ResetPassword />} />
                    <Route path='/profilePage' element={<ProfilePage />} />
                    <Route path='/cart' element={session && <CartPage key={session.user.id} session={session} />} />
                    <Route path='/mastermind' element={<Mastermind />} />
                    <Route path='/location' element={<Location />} />
                    <Route path='/privacy' element={<PrivacyPage />} />
                    <Route path='/signout' element={<SignoutRedirectPage />} />
                    <Route path='/newsletter' element={<NewsletterPage />} />
                    <Route path='/tnc' element={<TermsAndConditions />} />
                    <Route
                      path='/dashboard/*'
                      element={
                        !session ? <SignIn /> : <UserDashboard key={session.user.id} session={session} user={user} />
                      }
                    />
                  </Routes>
                </Box>
              </main>
              {location.pathname.startsWith('/onboarding') ||
              location.pathname.startsWith('/mentorRegistration') ||
              location.pathname.startsWith('/profileLinks') ||
              location.pathname.startsWith('/newsletter') ? (
                ''
              ) : (
                <Box sx={styles.container}>
                  <Footer />
                </Box>
              )}
            </Box>
            {location.pathname.startsWith('/onboarding') ||
            location.pathname.startsWith('/mentorRegistration') ||
            location.pathname.startsWith('/profileLinks') ||
            location.pathname.startsWith('/newsletter') ? (
              ''
            ) : (
              <a
                href='/payments'
                onClick={() => {
                  window.lintrk('track', { conversion_id: 13351812 });
                }}
                className={`origin-bottom-right fixed right-0 top-[50%] translate-y-[-50%] -rotate-90 font-bold text-white bg-primary-main px-[1.4rem] py-[0.8rem] cursor-pointer rounded-t-lg no-underline z-[9999] duration-100 hover:bg-black`}>
                Join Today
              </a>
            )}
          </Fragment>
        </ThemeProvider>
      )}
    </main>
  );
}

export default App;
